import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";

// The Treasurer — R1 company-facing metrics (R1-TR-01/02/03). Computed from the event log; shows the
// counts and withholds the verdict. Cost/unit-economics has real data (recorded model spend per task);
// funnel, retention, cash and the shadow meter are honest shells until there is data. Staff time is a
// stated, deliberate omission — never dressed up as a gap.
function usd(n) {
  return `$${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function range(pair, suffix = "", prefix = "") {
  if (!Array.isArray(pair)) return "—";
  return `${prefix}${pair[0]}${suffix}–${prefix}${pair[1]}${suffix}`;
}

const AWORD = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
  "Eleven", "Twelve"];
const aw = (n) => (n <= 12 ? AWORD[n] : String(n));

// The activation funnel (build brief §3) — the SAME snapshot the Rainmaker renders (§5). Measurement,
// not worklist: counts and a neutral bar, drop-off named between steps, names one click in. The bars
// are grey on purpose — a measurement is never a request, so no green/amber/red ramp.
function Activation({ a }) {
  const [open, setOpen] = useState(null);
  if (!a) return null;
  const f = a.funnel, s = a.supporting, recs = a.records;
  const headline = f.invited === 0
    ? "No owners invited yet."
    : f.filed_building === 0
      // Agreement is with the SUBJECT: "one of four ... has", "two of four ... have" (founder 30 Sep).
      ? `None of the ${aw(f.invited).toLowerCase()} invited ${f.invited === 1 ? "owner" : "owners"} ${f.invited === 1 ? "has" : "have"} filed a building yet.`
      : `${aw(f.filed_building)} of ${aw(f.invited).toLowerCase()} invited ${f.invited === 1 ? "owner" : "owners"} ${f.filed_building === 1 ? "has" : "have"} filed a building.`;
  const stages = [
    ["invited", "Invited", f.invited, null],
    ["set_password", "Set a password", f.set_password,
      f.dropoff.never_opened ? `${f.dropoff.never_opened} never opened it` : null],
    ["filed_building", "Filed a building", f.filed_building,
      f.dropoff.stopped_here ? `${f.dropoff.stopped_here} stopped here` : null],
    ["got_mark", "Got a mark", f.got_mark,
      f.dropoff.honest_no ? `${f.dropoff.honest_no} honest no` : null],
  ];
  const members = {
    invited: recs,
    set_password: recs.filter((r) => r.accepted_at),
    filed_building: recs.filter((r) => r.buildings > 0),
    got_mark: recs.filter((r) => r.got_mark),
  };
  const denom = Math.max(f.invited, 1);

  return (
    <section className="tr-block" data-testid="tr-activation">
      <p className="rm-eyebrow">— THE TREASURER · ACTIVATION</p>
      <h1 className="tr-act-headline" data-testid="tr-act-headline">{headline}</h1>
      <p className="tr-act-lede">Nobody pays in Release 1, so there is no revenue to read and no price to
        test. The only question this release answers is whether an invited owner comes back — and the
        answer is a building on the desk, not a sign-up.</p>

      <div className="tr-funnel-wrap">
        <div className="tr-funnel-head">
          <h2 className="tr-funnel-title">Invitation to first mark</h2>
          <span className="tr-funnel-hint">Click a step to see who is standing on it.</span>
        </div>
        {stages.map(([key, label, n, drop]) => (
          <div key={key} className="tr-stage-row">
            <button className={`tr-stage ${open === key ? "tr-stage-on" : ""}`}
                    data-testid={`tr-stage-${key}`} onClick={() => setOpen(open === key ? null : key)}>
              <span className="tr-stage-label">{label}</span>
              <span className="tr-stage-n">{n}</span>
              <span className="tr-stage-bar">
                <span className="tr-stage-fill" style={{ width: `${(n / denom) * 100}%` }} /></span>
              {drop && <span className="tr-stage-drop">{drop}</span>}
            </button>
            {open === key && (
              <div className="tr-stage-names" data-testid={`tr-names-${key}`}>
                {members[key].length === 0 ? <span className="tr-name-empty">Nobody here yet.</span>
                  : members[key].map((r) => (
                    <span key={r.id} className="tr-name-chip">{r.name || r.email}</span>))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="tr-support">
        <div className="tr-support-card" data-testid="tr-support-median">
          <p className="tr-support-tag">INVITE TO FIRST BUILDING</p>
          <p className="tr-support-num">{s.median_days_to_first_building == null ? "—"
            : `${s.median_days_to_first_building} day${s.median_days_to_first_building === 1 ? "" : "s"}`}</p>
          <p className="tr-support-body">Median from the invitation going out to the first building
            landing. The lag worth watching, not the click.</p>
        </div>
        <div className="tr-support-card" data-testid="tr-support-cameback">
          <p className="tr-support-tag">CAME BACK AFTER THE MARK</p>
          <p className="tr-support-num">{s.came_back.n} of {s.came_back.d}</p>
          <p className="tr-support-body">Signed back in after seeing a mark. This is the retention signal
            the release exists to find — worth more than any other number here.</p>
        </div>
        <div className="tr-support-card" data-testid="tr-support-asked">
          <p className="tr-support-tag">ASKED FOR A NEW LINK</p>
          <p className="tr-support-num">{s.asked_for_new_link}</p>
          <p className="tr-support-body">Requested a fresh invitation after theirs ran out, unprompted.
            Unprompted re-entry is worth more than a click.</p>
        </div>
      </div>

      <div className="tr-read" data-testid="tr-read">
        <p className="tr-read-tag">THE TREASURER'S READ</p>
        <p className="tr-read-body">
          {f.invited < 20
            ? "This is far too few to read a rate from, and it will stay that way for a while — treat every number here as a name, not a percentage. "
            : ""}
          {s.came_back.d > 0
            ? `Of the ${aw(s.came_back.d).toLowerCase()} who filed, ${aw(s.came_back.n).toLowerCase()} came back after seeing the mark. That is the signal this release exists to find.`
            : "No owner has reached a mark yet, so there is no retention to read — the one number to watch is still ahead."}
        </p>
      </div>
    </section>
  );
}

function Shell({ label, testid, reason, tag }) {
  return (
    <div className="tr-shell" data-testid={testid}>
      <div className="tr-shell-head">
        <span className="cc-metric-label">{label}</span>
        {tag && <span className="tr-tag">{tag}</span>}
      </div>
      <div className="tr-shell-reason">{reason}</div>
    </div>
  );
}

export default function TheTreasurer() {
  const { authedFetch } = useAuth();
  const [d, setD] = useState(null);
  const [act, setAct] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    authedFetch("/treasurer/section").then(setD).catch((e) => setErr(String(e)));
    authedFetch("/treasurer/activation").then(setAct).catch(() => {});
  }, [authedFetch]);

  if (err) return <div className="gk-err" data-testid="treasurer-error">{err}</div>;
  if (!d) return <div className="loading" data-testid="treasurer-loading">Loading…</div>;

  const c = d.cost;
  const ue = d.unit_economics;
  const maxSpend = Math.max(...(c.daily_trend || []).map((x) => x.spend_usd), 0.000001);

  return (
    <div data-testid="the-treasurer">
      <Activation a={act} />

      <div className="cc-fold-rule"><span>Cost &amp; internal metrics</span></div>

      <section className="tr-block" data-testid="tr-cost">
        <h2 className="cc-panel-title">Cost &amp; unit economics</h2>
        <p className="cc-panel-sub">{c.note}</p>

        <div className="tr-cost-top">
          <div className="tr-figure">
            <div className="tr-figure-num">{usd(c.today?.spend_usd)}</div>
            <div className="tr-figure-meta">recorded model spend today · {c.today?.calls ?? 0} calls</div>
          </div>
          <div className="tr-figure">
            <div className="tr-figure-num">{c.max_input_utilisation_pct ?? 0}%</div>
            <div className="tr-figure-meta">max input utilisation · {c.count_over_80pct ?? 0} over 80%</div>
          </div>
        </div>

        <div className="tr-sub" data-testid="tr-unit-economics">
          <h3 className="tr-sub-title">Unit economics — two figures, two jobs</h3>
          <div className="tr-ue-grid">
            <div className="tr-ue">
              <div className="tr-ue-num">{range(ue.steady_state_per_doc_usd, "", "$")}</div>
              <div className="tr-ue-label">steady-state per document</div>
              <div className="tr-ue-note">{ue.steady_state_note} <strong>Prices the product.</strong></div>
            </div>
            <div className="tr-ue">
              <div className="tr-ue-num">{range(ue.under_load_multiplier, "×")}</div>
              <div className="tr-ue-label">under-load multiplier</div>
              <div className="tr-ue-note">{ue.under_load_note} <strong>Sizes the ceilings — never prices the product.</strong></div>
              {ue.under_load_must_rederive && (
                <div className="tr-basis" data-testid="tr-under-load-basis">
                  <span className="tr-omission-tag">estimate · must re-derive</span>{ue.under_load_basis}
                </div>
              )}
            </div>
          </div>
          <div className="tr-omission" data-testid="tr-labour-omission">
            <span className="tr-omission-tag">deliberate omission</span> {ue.labour_note}
          </div>
        </div>

        <div className="tr-sub">
          <h3 className="tr-sub-title">Recorded spend, last 7 days</h3>
          <ul className="tr-trend" data-testid="tr-daily-trend">
            {(c.daily_trend || []).map((x, i) => (
              <li key={i} className="tr-trend-row">
                <span className="tr-trend-date">{x.date}</span>
                <span className="tr-trend-bar"><span className="tr-trend-fill" style={{ width: `${(x.spend_usd / maxSpend) * 100}%` }} /></span>
                <span className="tr-trend-num">{usd(x.spend_usd)}</span>
                <span className="tr-trend-calls">{x.calls} calls</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="tr-tables">
          <div className="tr-table" data-testid="tr-top-spenders">
            <h3 className="tr-sub-title">Costliest documents</h3>
            {(c.per_document_top_spenders || []).length === 0 ? (
              <div className="cc-empty">No documents have incurred model cost yet.</div>
            ) : (
              <ul className="tr-rows">
                {c.per_document_top_spenders.slice(0, 6).map((t, i) => (
                  <li key={i} className="tr-row"><span className="mono">{t.document_id}</span>
                    <span className="tr-row-num">{usd(t.spend_usd)} · {t.calls} calls</span></li>
                ))}
              </ul>
            )}
          </div>
          <div className="tr-table" data-testid="tr-by-agent">
            <h3 className="tr-sub-title">Spend by agent</h3>
            {(c.by_agent || []).length === 0 ? (
              <div className="cc-empty">No agent has incurred model cost yet.</div>
            ) : (
              <ul className="tr-rows">
                {c.by_agent.map((a, i) => (
                  <li key={i} className="tr-row"><span className="mono">{a.agent || "—"}</span>
                    <span className="tr-row-num">{usd(a.spend_usd)} · {a.calls} calls</span></li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      <section className="tr-block" data-testid="tr-metric-store">
        <h2 className="cc-panel-title">The metric store</h2>
        <p className="cc-panel-sub">{d.metric_store.note}</p>
        {!d.metric_store.source_present && (
          <div className="tr-omission" data-testid="tr-metric-source-gap">
            <span className="tr-omission-tag">no source yet</span>
            The event log has received no events — the agents' runs are recorded to agent_run but not
            yet emitted here. This store is wired and waiting, not miscounting.
          </div>
        )}
        <div className="tr-cost-top">
          <div className="tr-figure">
            <div className="tr-figure-num">{d.metric_store.documents_filed}</div>
            <div className="tr-figure-meta">documents filed (from the event log)</div>
          </div>
          <div className="tr-figure">
            <div className="tr-figure-num">{d.metric_store.events_total}</div>
            <div className="tr-figure-meta">events in the log</div>
          </div>
        </div>
      </section>

      <div className="cc-fold-rule"><span>Not yet — real metrics, no data</span></div>
      <div className="cc-metrics tr-shells">
        <Shell label="Cash" testid="tr-shell-cash" tag="by design" reason={d.cash.absent_reason} />
        <Shell label="Shadow credit meter" testid="tr-shell-shadow" reason={d.shadow_credit_meter.absent_reason} />
      </div>
    </div>
  );
}
